import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { useAuth } from "@/context/AuthContext";
import { KpiCard, Panel, PageHeader, StatusPill, RiskBadge, RISK_ORDER, Loading } from "@/components/portal/widgets";
import LiveMap, { driverColor, isStale } from "@/components/portal/LiveMap";
import { useLiveRefresh, UpdatedAgo, ago } from "@/lib/liveRefresh";
import UsageMeter from "@/components/portal/UsageMeter";
import { fmtToday } from "@/lib/time";
import AlertResolveDialog from "@/components/portal/AlertResolveDialog";
import { Activity, CheckCircle2, AlertTriangle, Users, Truck, ShieldAlert, Clock, FileWarning } from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [attn, setAttn] = useState([]);
  const [brief, setBrief] = useState(null);
  const [eod, setEod] = useState(null);
  const [error, setError] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [live, setLive] = useState([]);

  const load = useCallback(async () => {
    const [a, b, risky, attnRes, br, ed, lm] = await Promise.all([
      fleetApi.get("/dashboard"),
      fleetApi.get("/drivers"),
      Promise.all([
        fleetApi.get("/trips?risk=missed&limit=200"),
        fleetApi.get("/trips?risk=predicted_late&limit=200"),
        fleetApi.get("/trips?risk=at_risk&limit=200"),
      ]).then((rs) => rs.flatMap((r) => (Array.isArray(r.data) ? r.data : r.data?.trips || []))),
      fleetApi.get("/attention-queue").catch(() => ({ data: [] })),
      fleetApi.get("/daily-brief").catch(() => ({ data: null })),
      fleetApi.get("/end-of-day").catch(() => ({ data: null })),
      fleetApi.get("/live-map").catch(() => ({ data: [] })),
    ]);
    setD(a.data); setDrivers(b.data);
    setTrips(risky.sort((x, y) => (RISK_ORDER[x.risk] ?? 9) - (RISK_ORDER[y.risk] ?? 9) || (y.predicted_delay_min || 0) - (x.predicted_delay_min || 0)));
    setAttn(Array.isArray(attnRes.data) ? attnRes.data : []);
    setBrief(br.data); setEod(ed.data);
    setLive(Array.isArray(lm.data) ? lm.data : []);
  }, []);

  useEffect(() => { load().catch(() => setError(true)); }, [load]);
  const updatedAt = useLiveRefresh(load, 20000);

  if (error) return <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-5 py-8 text-center text-sm text-red-300" data-testid="dashboard-error">Couldn't load live operations data. Please refresh to try again.</div>;
  if (!d) return <Loading />;

  const goAttn = (it) => {
    if (it.trip_id) navigate(`/portal/trips/${it.trip_id}`);
    else if (it.driver_id) navigate(`/portal/drivers/${it.driver_id}`);
    else navigate("/portal/incidents");
  };
  const ackAlert = async (e, it) => {
    e.stopPropagation();
    try { await fleetApi.post(`/alerts/${it.id}/acknowledge`, {}); setAttn((p) => p.map((x) => (x.id === it.id ? { ...x, status: "acknowledged" } : x))); toast.success("Alert acknowledged."); }
    catch (err) { toast.error(fleetError(err, "Couldn't acknowledge.")); }
  };
  const resolveAlert = (e, it) => { e.stopPropagation(); setResolving(it); };
  const onResolved = (id) => setAttn((p) => p.filter((x) => x.id !== id));
  const urgentCount = attn.filter((x) => x.severity === "urgent").length;

  const mapDrivers = live.map((x) => ({ id: x.driver_id, name: x.name || x.full_name, lat: x.lat, lng: x.lng, last_ping: x.last_ping, marker: x.marker, trip: x.current_trip }));
  const located = mapDrivers.filter((x) => x.last_ping);
  const unlocated = mapDrivers.length - located.length;
  const atRisk = trips.slice(0, 14);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Welcome back, ${(user?.name || "").split(" ")[0]}`}
        subtitle={`${d.org_name} · live operations`}>
        <UpdatedAgo at={updatedAt} testid="dashboard-live-updated" />
      </PageHeader>

      {brief && (
        <Panel className="mb-6" title="Start-of-Day Brief" testid="panel-daily-brief"
          action={<span className="font-mono-data text-xs text-slate-500">{fmtToday()}</span>}>
          <div className="p-5">
            {brief.narrative && <p className="whitespace-pre-line text-sm leading-relaxed text-slate-300" data-testid="brief-narrative">{brief.narrative}</p>}
            {Array.isArray(brief.risks) && brief.risks.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {brief.risks.map((r, i) => (
                  <button key={i} onClick={() => navigate("/portal/dispatch?risk=at_risk")} data-testid={`brief-risk-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs text-amber-300 transition-colors hover:bg-amber-500/20">
                    <AlertTriangle className="h-3 w-3 shrink-0" />{r}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-3 border-t border-slate-800 pt-4 sm:grid-cols-4">
              {[
                ["Unassigned", brief.facts?.unassigned_trips ?? 0, "/portal/dispatch?risk=unassigned"],
                ["Open alerts", brief.facts?.open_alerts ?? 0, "#panel-attention"],
                ["Completion", eod ? `${eod.completion_rate}%` : "—", "/portal/reports"],
                ["On-time", eod ? `${eod.on_time_rate}%` : "—", "/portal/reports"],
              ].map(([label, value, to]) => (
                <button key={label} onClick={() => (to.startsWith("#") ? document.querySelector('[data-testid="panel-attention"]')?.scrollIntoView({ behavior: "smooth" }) : navigate(to))}
                  data-testid={`brief-fact-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-md p-1 text-left transition-colors hover:bg-slate-800/50">
                  <div className="font-mono-data text-2xl font-bold text-white">{value}</div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                </button>
              ))}
            </div>
          </div>
        </Panel>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard icon={Activity} label="In Progress" value={Math.max(0, d.trips_today - d.trips_completed)} sub={`${d.trips_today} today`} accent="blue" testid="kpi-active-trips" to="/portal/dispatch" />
        <KpiCard icon={AlertTriangle} label="At Risk" value={d.at_risk} sub={`${d.predicted_late} predicted late`} accent="amber" testid="kpi-late-risk" to="/portal/dispatch?risk=at_risk" />
        <KpiCard icon={CheckCircle2} label="On Track" value={d.on_track} sub={`${d.missed} missed`} accent="emerald" testid="kpi-on-time" to="/portal/dispatch?risk=on_track" />
        <KpiCard icon={ShieldAlert} label="Open Incidents" value={d.open_incidents} sub={`${d.vehicle_issues} vehicle issues`} accent="red" testid="kpi-incidents" to="/portal/incidents" />
        <KpiCard icon={Users} label="Drivers Active" value={d.drivers_active} accent="slate" testid="kpi-drivers" to="/portal/fleet" />
        <KpiCard icon={Truck} label="Vehicles Active" value={d.vehicles_active} accent="slate" testid="kpi-vehicles" to="/portal/fleet?tab=vehicles" />
        <KpiCard icon={Clock} label="Completed" value={d.trips_completed.toLocaleString()} accent="emerald" testid="kpi-completed" to="/portal/dispatch" />
        <KpiCard icon={FileWarning} label="Licenses Expiring" value={d.licenses_expiring + d.licenses_expired} sub={`${d.licenses_expired} expired`} accent="amber" testid="kpi-licenses" to="/portal/incidents?tab=compliance" />
      </div>

      <UsageMeter driversUsed={d.drivers_active} vehiclesUsed={d.vehicles_active} />

      {attn.length > 0 && (
        <Panel className="mt-6" title="Needs Attention" testid="panel-attention"
          action={<span className="rounded-full bg-red-500/15 px-2 py-0.5 font-mono-data text-xs font-bold text-red-400">{urgentCount} urgent</span>}>
          <div className="max-h-[320px] divide-y divide-slate-800 overflow-y-auto">
            {attn.map((it) => (
              <div key={it.id} data-testid={`attn-${it.id}`}
                className="flex items-start justify-between gap-3 px-5 py-3 transition-colors hover:bg-slate-800/40">
                <button onClick={() => goAttn(it)} data-testid={`attn-open-${it.id}`}
                  className="flex min-w-0 flex-1 items-start gap-3 text-left">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${it.severity === "urgent" ? "bg-red-500" : "bg-amber-400"}`} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white">{it.problem}{it.status === "acknowledged" ? " · ack'd" : ""}</div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">{it.recommended_action}{it.driver_name ? ` · ${it.driver_name}` : ""}</div>
                  </div>
                </button>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button onClick={(e) => ackAlert(e, it)} data-testid={`attn-ack-${it.id}`}
                    className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] font-medium text-slate-300 transition-colors hover:bg-slate-700">Ack</button>
                  <button onClick={(e) => resolveAlert(e, it)} data-testid={`attn-resolve-${it.id}`}
                    className="rounded border border-emerald-700/50 bg-emerald-600/15 px-2 py-0.5 text-[11px] font-medium text-emerald-300 transition-colors hover:bg-emerald-600/25">Resolve</button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1.6fr_1fr] [&>*]:min-w-0">
        <Panel title="Live GPS — Fleet Positions" testid="panel-live-map"
          action={<span className="font-mono-data text-xs text-slate-500" data-testid="live-map-count">{located.length} reporting · {unlocated} no location</span>}>
          <div className="p-4">
            <LiveMap drivers={mapDrivers} onSelect={(id) => navigate(`/portal/drivers/${id}`)} />
            <div className="mt-3 flex flex-wrap gap-2" data-testid="live-map-legend">
              {located.map((x) => (
                <button key={x.id} onClick={() => navigate(`/portal/drivers/${x.id}`)} data-testid={`legend-driver-${x.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] text-slate-200 hover:bg-slate-800">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: driverColor(x.id), opacity: isStale(x.last_ping) ? 0.5 : 1 }} />
                  {x.name}
                  <span className={isStale(x.last_ping) ? "text-amber-400" : "text-slate-500"}>{isStale(x.last_ping) ? `stale · ${ago(x.last_ping)}` : ago(x.last_ping)}</span>
                  {x.trip?.external_trip_id && <span className="text-blue-300">· {x.trip.external_trip_id}</span>}
                </button>
              ))}
              {located.length === 0 && <p className="text-xs text-slate-500" data-testid="live-map-none">No driver has shared a live position yet. Positions appear once a driver is on duty in the app with location allowed.</p>}
            </div>
          </div>
        </Panel>

        <Panel title="Late-Trip Prevention" testid="panel-late-risk"
          action={<span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-mono-data text-xs font-bold text-amber-400">{atRisk.length}</span>}>
          <div className="max-h-[360px] divide-y divide-slate-800 overflow-y-auto">
            {atRisk.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">No at-risk trips right now.</div>}
            {atRisk.map((t) => (
              <button key={t.id} onClick={() => navigate(`/portal/trips/${t.id}`)} data-testid={`risk-trip-${t.id}`}
                className="flex w-full items-center justify-between px-5 py-3 text-left transition-colors hover:bg-slate-800/40">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-white">{t.rider_name} · <span className="text-slate-400">{t.assigned_driver_name || "Unassigned"}</span></div>
                  <div className="mt-0.5 truncate text-xs text-slate-500">{t.dropoff_name || t.dropoff_address}</div>
                </div>
                <RiskBadge trip={t} testid={`risk-badge-${t.id}`} />
              </button>
            ))}
          </div>
        </Panel>
      </div>

      <AlertResolveDialog alert={resolving} open={!!resolving}
        onOpenChange={(v) => { if (!v) setResolving(null); }} onResolved={onResolved} />
    </div>
  );
}
